import { XIcon } from "@heroicons/react/outline";
import {
  ArrowLeftRightIcon,
  DownloadIcon,
  Loader2Icon,
  MaximizeIcon,
  PauseIcon,
  PlayIcon,
  Volume2Icon,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { sizeConfig } from "@/components/ui/MediaBlocks/constants";
import { useMediaFullscreen } from "@/components/ui/MediaBlocks/media-fullscreen-context";
import { AudioBlockProps } from "@/components/ui/MediaBlocks/types";
import { downloadMedia } from "@/components/ui/MediaBlocks/utils";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { SimpleWaveform } from "@/components/ui/waveform";
import { cn } from "@/lib/utils";

const formatTime = (seconds: number) => {
  if (!isFinite(seconds)) {
    return "0:00";
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const isPlayableDuration = (value: number) => isFinite(value) && value > 0;

interface FullscreenAudioPlayerProps {
  media: { url: string; title?: string };
  initialDuration: number;
}

const FullscreenAudioPlayer = ({ media, initialDuration }: FullscreenAudioPlayerProps) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(initialDuration);

  const audioRef = useRef<HTMLAudioElement>(null);
  const animationRef = useRef<number | null>(null);

  const progress = duration > 0 ? currentTime / duration : 0;

  const updateProgress = useCallback(() => {
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      setCurrentTime(audio.currentTime);
      animationRef.current = requestAnimationFrame(updateProgress);
    }
  }, []);

  useEffect(() => {
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateProgress);
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, updateProgress]);

  const handleLoadedMetadata = () => {
    if (audioRef.current && isPlayableDuration(audioRef.current.duration)) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => setIsPlaying(false);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (audio) {
      if (isPlaying) {
        audio.pause();
      } else {
        void audio.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleScrub = (newProgress: number) => {
    if (duration > 0) {
      const newTime = newProgress * duration;
      setCurrentTime(newTime);
    }
  };

  const handleSeek = (newProgress: number) => {
    const audio = audioRef.current;
    if (audio && isPlayableDuration(duration)) {
      const newTime = newProgress * duration;
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  return (
    <div className="w-full p-6">
      <audio
        ref={audioRef}
        src={media.url}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />
      <SimpleWaveform
        progress={progress}
        onSeek={handleSeek}
        onScrub={handleScrub}
        height={64}
        barCount={60}
        waveColor="#E5E7EB"
        progressColor="#2563EB"
        className="mb-6"
      />
      <div className="flex items-center justify-between">
        <span className="w-12 text-sm font-medium text-gray-600">{formatTime(currentTime)}</span>
        <Button
          variant="default"
          size="icon"
          className="!h-14 !w-14 rounded-full"
          onClick={togglePlay}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <PauseIcon className="!h-6 !w-6 fill-current" />
          ) : (
            <PlayIcon className="!h-6 !w-6 fill-current" />
          )}
        </Button>
        <span className="w-12 text-right text-sm font-medium text-gray-600">
          {formatTime(duration)}
        </span>
      </div>
    </div>
  );
};

export const AudioBlock = ({
  media,
  onDelete,
  onReplace,
  readonly = true,
  size = "md",
  isLoading = false,
}: AudioBlockProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasLoadError, setHasLoadError] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const animationRef = useRef<number | null>(null);

  const { openFullscreen } = useMediaFullscreen();

  const isXs = size === "xs";
  const progress = duration > 0 ? currentTime / duration : 0;
  const hasPlayableAudio = isPlayableDuration(duration);
  const showLoading = Boolean(media.url) && !hasPlayableAudio && (isLoading || !hasLoadError);
  const showError = Boolean(media.url) && !hasPlayableAudio && hasLoadError && !isLoading;

  const updateProgress = useCallback(() => {
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      setCurrentTime(audio.currentTime);
      animationRef.current = requestAnimationFrame(updateProgress);
    }
  }, []);

  const syncDurationFromElement = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (isPlayableDuration(audio.duration)) {
      setDuration(audio.duration);
      setHasLoadError(false);
    }
  }, []);

  useEffect(() => {
    setDuration(0);
    setCurrentTime(0);
    setIsPlaying(false);
    setHasLoadError(false);

    const frame = requestAnimationFrame(() => {
      syncDurationFromElement();
    });
    return () => cancelAnimationFrame(frame);
  }, [media.url, syncDurationFromElement]);

  useEffect(() => {
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateProgress);
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, updateProgress]);

  const handleLoadError = () => {
    if (!isPlayableDuration(audioRef.current?.duration ?? 0)) {
      setHasLoadError(true);
    }
    setIsPlaying(false);
  };

  const handleEnded = () => setIsPlaying(false);

  const togglePlay = () => {
    if (showLoading || showError) {
      return;
    }
    const audio = audioRef.current;
    if (audio) {
      if (isPlaying) {
        audio.pause();
      } else {
        void audio.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleScrub = (newProgress: number) => {
    if (duration > 0) {
      const newTime = newProgress * duration;
      setCurrentTime(newTime);
    }
  };

  const handleSeek = (newProgress: number) => {
    const audio = audioRef.current;
    if (audio && isPlayableDuration(duration)) {
      const newTime = newProgress * duration;
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleOpenFullscreen = () => {
    if (showLoading || showError) {
      return;
    }
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    }

    const headerIcon = (
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
        <Volume2Icon className="h-5 w-5 text-blue-600" />
      </div>
    );

    openFullscreen({
      title: media.title || "Audio File",
      downloadUrl: media.url,
      downloadFilename: media.title || "audio",
      variant: "light",
      maxWidth: "lg",
      headerIcon,
      headerSubtitle: formatTime(duration),
      content: <FullscreenAudioPlayer media={media} initialDuration={duration} />,
    });
  };

  const statusLabel = showError
    ? "Couldn't load audio"
    : `${formatTime(currentTime)} / ${formatTime(duration)}`;

  const displayTitle = showLoading ? "Generating Audio" : media.title || "Audio File";

  const tooltipContent = (
    <div className="flex flex-col gap-0.5">
      <span className="font-medium">{displayTitle}</span>
      <span className="truncate text-gray-300">{media.url}</span>
    </div>
  );

  const renderXsIcon = () => {
    if (showLoading) {
      return <Loader2Icon className="h-8 w-8 animate-spin text-gray-400" />;
    }
    if (isPlaying) {
      return <PauseIcon className="h-8 w-8 text-gray-500" />;
    }
    return <Volume2Icon className="h-8 w-8 text-gray-500" />;
  };

  const renderPlayButtonIcon = () => {
    if (showLoading) {
      return <Loader2Icon className={cn("animate-spin", sizeConfig.playIcon[size])} />;
    }
    if (isPlaying) {
      return <PauseIcon className={cn("fill-current", sizeConfig.playIcon[size])} />;
    }
    return <PlayIcon className={cn("fill-current", sizeConfig.playIcon[size])} />;
  };

  const playButtonAriaLabel = (() => {
    if (showLoading) {
      return "Loading audio";
    }
    if (showError) {
      return "Audio unavailable";
    }
    if (isPlaying) {
      return "Pause";
    }
    return "Play";
  })();

  const renderAudioElement = () => (
    <audio
      ref={audioRef}
      src={media.url}
      preload="auto"
      onLoadedMetadata={syncDurationFromElement}
      onDurationChange={syncDurationFromElement}
      onCanPlay={syncDurationFromElement}
      onError={handleLoadError}
      onEnded={handleEnded}
    />
  );

  if (isXs) {
    return (
      <>
        {renderAudioElement()}
        <BasicTooltip content={tooltipContent} side="top">
          <div
            className={cn(
              "relative flex h-20 w-20 items-center justify-center rounded-lg border border-gray-200 bg-gray-100 transition-opacity",
              showLoading || showError ? "cursor-wait" : "cursor-pointer hover:opacity-80",
            )}
            onClick={handleOpenFullscreen}
            aria-busy={showLoading}
          >
            {renderXsIcon()}
          </div>
        </BasicTooltip>
      </>
    );
  }

  return (
    <div
      className={cn(
        "group/media rounded-lg border border-gray-200 bg-white",
        sizeConfig.padding[size],
      )}
      aria-busy={showLoading}
    >
      {renderAudioElement()}

      {/* Title and actions */}
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-900">{displayTitle}</span>
        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover/media:opacity-100">
          <Button
            variant="ghost"
            size="tinyIcon"
            onClick={handleOpenFullscreen}
            aria-label="Open player"
            disabled={showLoading || showError}
          >
            <MaximizeIcon className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="tinyIcon"
            onClick={() => downloadMedia(media.url, media.title || "audio")}
            aria-label="Download audio"
            disabled={showLoading || showError}
          >
            <DownloadIcon className="h-3.5 w-3.5" />
          </Button>
          {!readonly && onReplace && (
            <>
              <Button
                variant="ghost"
                size="tinyIcon"
                onClick={() => fileInputRef.current?.click()}
                aria-label="Replace audio"
                disabled={showLoading || showError}
              >
                <ArrowLeftRightIcon className="h-3.5 w-3.5" />
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="audio/*"
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) {
                    onReplace(file);
                  }
                }}
              />
            </>
          )}
          {!readonly && onDelete && (
            <Button
              variant="ghost"
              size="tinyIcon"
              onClick={onDelete}
              aria-label="Delete audio"
              disabled={showLoading || showError}
            >
              <XIcon className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Player controls */}
      <div className="flex items-center gap-3">
        <Button
          variant="default"
          size="icon"
          className={cn("shrink-0 rounded-full", sizeConfig.playButton[size])}
          onClick={togglePlay}
          disabled={showLoading || showError}
          aria-label={playButtonAriaLabel}
        >
          {renderPlayButtonIcon()}
        </Button>
        <SimpleWaveform
          progress={showLoading || showError ? 0 : progress}
          onSeek={showLoading || showError ? undefined : handleSeek}
          onScrub={showLoading || showError ? undefined : handleScrub}
          height={32}
          barCount={80}
          waveColor="#E5E7EB"
          progressColor="#2563EB"
          className={cn("flex-1", showLoading && "animate-pulse opacity-60")}
        />
        <span
          className={cn(
            "shrink-0 whitespace-nowrap text-sm",
            showError ? "text-red-500" : "text-gray-500",
          )}
        >
          {statusLabel}
        </span>
      </div>
    </div>
  );
};
